// ============================================
// COMPONENT - Barcode Scanner Modal
// ============================================

import { useEffect, useRef, useState } from 'react';
import { MdClose, MdFlashOn, MdFlashOff, MdCameraswitch } from 'react-icons/md';
import { FaBarcode } from 'react-icons/fa';
import { useCameraBarcodeScan } from '../hooks/useCameraBarcodeScan';

interface BarcodeScannerModalProps {
  /**
   * Si el modal está abierto
   */
  isOpen: boolean;
  
  /**
   * Callback para cerrar el modal
   */
  onClose: () => void;
  
  /**
   * Callback cuando se escanea un código
   */
  onScan: (barcode: string) => void;
  
  /**
   * Título del modal
   */
  title?: string;
}

/**
 * Modal con escáner de códigos de barras usando la cámara.
 * Detecta automáticamente códigos y ejecuta el callback onScan.
 * 
 * @example
 * ```tsx
 * <BarcodeScannerModal
 *   isOpen={showScanner}
 *   onClose={() => setShowScanner(false)}
 *   onScan={(code) => {
 *     console.log('Código escaneado:', code);
 *     // Buscar producto...
 *   }}
 * />
 * ```
 */
export const BarcodeScannerModal = ({
  isOpen,
  onClose,
  onScan,
  title = 'Escanear Código de Barras'
}: BarcodeScannerModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);

  const {
    startScanning,
    stopScanning,
    isScanning,
    error,
    detectedCode,
  } = useCameraBarcodeScan({
    enabled: isOpen,
    onScan: (code) => {
      // Mostrar feedback visual
      setScanSuccess(true);
      
      // Ejecutar callback
      onScan(code);
      
      // Cerrar modal después de un delay
      setTimeout(() => {
        setScanSuccess(false);
        onClose();
      }, 500);
    },
  });

  // Iniciar escaneo cuando se abre el modal
  useEffect(() => {
    if (isOpen && videoRef.current) {
      console.log('Modal abierto, video disponible, iniciando escaneo...');
      // Pequeño delay para asegurar que el DOM está listo
      const timer = setTimeout(() => {
        startScanning(videoRef.current);
      }, 200);
      
      return () => {
        clearTimeout(timer);
        console.log('Cerrando modal, deteniendo escaneo...');
        stopScanning();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Solo ejecutar cuando isOpen cambie

  // Toggle flash (si está disponible)
  const toggleFlash = async () => {
    const videoTrack = videoRef.current?.srcObject instanceof MediaStream
      ? videoRef.current.srcObject.getVideoTracks()[0]
      : null;

    if (videoTrack && 'torch' in videoTrack.getCapabilities()) {
      try {
        await videoTrack.applyConstraints({
          // @ts-ignore - torch no está en los tipos pero existe en algunos dispositivos
          advanced: [{ torch: !flashEnabled }]
        });
        setFlashEnabled(!flashEnabled);
      } catch (err) {
        console.error('Error al controlar el flash:', err);
      }
    }
  };

  // Cambiar entre cámara frontal y trasera
  const switchCamera = () => {
    stopScanning();
    setTimeout(() => {
      if (videoRef.current) {
        startScanning(videoRef.current);
      }
    }, 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FaBarcode className="text-2xl" />
            {title}
          </h2>
          
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <MdClose className="text-2xl text-white" />
          </button>
        </div>
      </div>

      {/* Video Preview */}
      <div className="relative w-full h-full flex items-center justify-center">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          playsInline
          muted
        />

        {/* Overlay con marco de escaneo */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Oscurecer los bordes */}
          <div className="absolute inset-0 bg-black/50" />
          
          {/* Marco de escaneo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div 
              className={`relative w-80 h-52 border-4 rounded-2xl transition-all duration-300 ${
                scanSuccess 
                  ? 'border-green-500 shadow-lg shadow-green-500/50' 
                  : 'border-white shadow-lg'
              }`}
              style={{
                boxShadow: scanSuccess 
                  ? '0 0 30px rgba(34, 197, 94, 0.8), inset 0 0 30px rgba(34, 197, 94, 0.2)'
                  : '0 0 0 9999px rgba(0, 0, 0, 0.5)'
              }}
            >
              {/* Esquinas decorativas */}
              <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-white rounded-tl-xl" />
              <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-white rounded-tr-xl" />
              <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-white rounded-bl-xl" />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-white rounded-br-xl" />
              
              {/* Línea de escaneo animada (láser rojo) */}
              {!scanSuccess && (
                <div className="absolute inset-0 overflow-hidden rounded-xl">
                  <div 
                    className="w-full h-0.5 animate-scan"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(239, 68, 68, 0.3) 20%, rgba(239, 68, 68, 1) 50%, rgba(239, 68, 68, 0.3) 80%, transparent 100%)',
                      boxShadow: '0 0 20px rgba(239, 68, 68, 0.8), 0 0 40px rgba(239, 68, 68, 0.4)'
                    }}
                  />
                </div>
              )}
              
              {/* Feedback visual de éxito */}
              {scanSuccess && (
                <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 rounded-xl">
                  <div className="text-center">
                    <div className="text-6xl mb-2">✓</div>
                    <p className="text-white font-bold text-lg">¡Código detectado!</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Instrucciones */}
          {!scanSuccess && (
            <div className="absolute bottom-32 left-0 right-0 text-center px-4">
              <div className="inline-block bg-black/70 backdrop-blur-sm rounded-2xl px-6 py-3">
                <p className="text-white text-sm font-medium">
                  Coloca el código de barras dentro del marco
                </p>
                {detectedCode && (
                  <p className="text-green-400 text-xs mt-1">
                    Procesando código...
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-6">
        <div className="flex items-center justify-center gap-6">
          {/* Botón Flash */}
          <button
            onClick={toggleFlash}
            className={`p-4 rounded-full transition-all ${
              flashEnabled 
                ? 'bg-yellow-500 hover:bg-yellow-600' 
                : 'bg-white/20 hover:bg-white/30'
            }`}
            title={flashEnabled ? 'Apagar flash' : 'Encender flash'}
          >
            {flashEnabled ? (
              <MdFlashOn className="text-2xl text-white" />
            ) : (
              <MdFlashOff className="text-2xl text-white" />
            )}
          </button>

          {/* Botón Cambiar Cámara */}
          <button
            onClick={switchCamera}
            className="p-4 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            title="Cambiar cámara"
          >
            <MdCameraswitch className="text-2xl text-white" />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="absolute top-20 left-4 right-4 z-20">
          <div className="bg-red-500 text-white px-4 py-3 rounded-xl shadow-lg">
            <p className="font-medium">Error</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {!isScanning && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-4" />
            <p className="text-white text-lg font-medium">Iniciando cámara...</p>
          </div>
        </div>
      )}

      {/* Estilos para la animación de escaneo */}
      <style>{`
        @keyframes scan {
          0% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(192px);
          }
          100% {
            transform: translateY(0);
          }
        }
        
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
